import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function NewhamCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Newham Pothole Claims | 367km of Residential Roads | Fixtyer"
        description="Newham Council spends £8.9m on roads in 2025/26 and earns a GREEN spend rating, yet 85% of its network is unclassified roads surveyed only 50% each year — and the council admits condition scores may not capture underlying deterioration."
        canonical="/councils/newham"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Newham', url: '/councils/newham' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Newham spends nearly thirteen times its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect under Section 58 of the Highways Act 1980 — not aggregate spend. Newham is AMBER overall because condition data shows persistent problems on a borough where 367.5km of unclassified roads make up 85% of the carriageway network, and the council itself warns surface-level scores may not capture underlying deterioration.',
          },
          {
            question: 'What if my pothole was on a residential street in Newham?',
            answer: 'Unclassified roads account for 367.5km — 85% of Newham\'s 432.21km carriageway network. The council surveys 50% of U-roads each year, split geographically, meaning half the residential network has no fresh condition data in any given year. At the last survey in 2023, 18% of U-roads were still in RED condition — down from 30.1% in 2020, but still roughly one in five residential roads.',
          },
          {
            question: 'Does Newham\'s admission that condition data may not tell the full story help my claim?',
            answer: 'It can. Newham\'s own transparency report states that improving surface-level condition scores on U-roads "may not always capture underlying deterioration or the compounding effects of historical underinvestment, which can mask the true long-term condition of the roads." That is the council acknowledging its published data may understate the problem — which weakens any Section 58 defence that relies solely on survey scores.',
          },
          {
            question: 'Who maintains the A-road where I hit the pothole — Newham or TfL?',
            answer: 'It depends on the road. Transport for London maintains the TfL Road Network in Newham, including the A13, A1020 Royal Docks Road, Lower Lea Crossing, A406, and parts of the A117. Newham Council maintains the remainder, including the Principal Road Network. A-road condition data in the transparency report comes from TfL surveys — check whether your incident was on a TfL or council-maintained stretch before submitting a claim.',
          },
          {
            question: 'Newham says it needs £11 million a year — does underfunding strengthen my case?',
            answer: 'It adds context, not an automatic win. Newham\'s 2023 projection modelling calculated a minimum annual budget of £11 million to maintain the network at its current condition, while the council averages £10 million annually since 2016. The report states that without £11 million, "the condition of the road network will continue to decline." That documents a funding gap the council knows about — but you still need evidence the specific defect was unreasonably left unrepaired.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Newham Council"
        councilSlug="newham"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/newham"
        headline="Newham Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Newham</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Newham: 367km of Residential Roads on a Two-Year Survey Cycle
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Newham earns a <strong className="text-emerald-400">GREEN</strong> spend rating — projecting
            £8.9 million in capital maintenance in 2025/26 against a DfT allocation of just £692,000.
            Yet the overall DfT rating is <strong className="text-amber-400">AMBER</strong>, because
            85% of the borough's carriageway is unclassified roads, the council only condition-surveys
            half of them each year, and its own report admits surface scores
            <strong className="text-rose-400"> may not capture what's underneath</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">18%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2023)</div>
            <div className="text-brand-300 text-sm mt-1">
              Down from 30.1% in 2020 — but the council warns improving scores "may not always capture
              underlying deterioration." No U-road survey was undertaken in 2024 at all.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* Network at a glance */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A Borough Split Between TfL and the Council
          </h2>
          <p className="text-surface-600 mb-8">
            Newham's 432.21km carriageway network — who maintains what, and where the potholes land
          </p>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-8">
            <div className="grid sm:grid-cols-4 gap-4 mb-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-900">50.93km</div>
                <div className="text-sm text-surface-600">A-roads (11.8%)</div>
                <div className="text-xs text-surface-500 mt-1">TfL surveys annually</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-900">13.79km</div>
                <div className="text-sm text-surface-600">B-roads (3.2%)</div>
                <div className="text-xs text-surface-500 mt-1">Council surveys annually</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">367.50km</div>
                <div className="text-sm text-rose-800">U-roads (85.0%)</div>
                <div className="text-xs text-surface-500 mt-1">50% surveyed each year</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-900">432.21km</div>
                <div className="text-sm text-surface-600">Total carriageway</div>
                <div className="text-xs text-surface-500 mt-1">Plus 706.46km footways</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Transport for London maintains the TfL Road Network — the A13, A1020 Royal Docks Road,
              Lower Lea Crossing, A406, and parts of the A117. Newham Council maintains everything else,
              including the Principal Road Network. If your pothole was on a TfL route, your claim goes
              to TfL, not the council.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Other Assets the Council Owns</h3>
              <ul className="text-surface-600 text-sm space-y-1">
                <li>• 76 highway structures (bridges, footbridges, culverts, subways, retaining walls)</li>
                <li>• 19,238 streetlights and 300 belisha beacons</li>
                <li>• 20,365 drainage assets, including gullies</li>
                <li>• Highway infrastructure valued at approximately £1.3 billion</li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Residential Road Dominance</h3>
              <p className="text-amber-800 text-sm">
                At 367.5km, unclassified roads are not a minority concern — they are the network.
                Most pothole claims in Newham will land on a U-road. That makes the council's
                alternate-year survey regime and its own caveats about surface-level scoring
                directly relevant to your Section 58 argument.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of published condition data from Newham's own transparency report — improving
            headlines, persistent AMBER reality
          </p>

          {/* A-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (50.93km — 11.8% of network): TfL data, slipping</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">8.5%</td>
                    <td className="px-4 py-2 text-center">31.8%</td>
                    <td className="px-4 py-2 text-center">59.7%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-surface-50">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No surveys undertaken by TfL</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">11%</td>
                    <td className="px-4 py-2 text-center">30%</td>
                    <td className="px-4 py-2 text-center">58%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">10%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">34%</td>
                    <td className="px-4 py-2 text-center font-bold">56%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono">2024</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>Anticipated from TfL June 2025</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              RED A-roads rose from 8.5% to 11% between 2020 and 2022, and amber-condition roads climbed
              to 34% by 2023. Good-condition A-roads fell from 59.7% to 56%. These are TfL-maintained
              routes — but the trend tells you what heavy traffic does to Newham's principal corridors.
            </p>
          </div>

          {/* B-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B-roads (13.79km — 3.2% of network): RED declining, gaps in data</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">B-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">14%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">14%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-amber-50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">No surveys undertaken by LB Newham</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">12%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">9%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 text-sm">
              Newham's report publishes RED percentages only for B-roads — not the full amber/green
              breakdown. RED B-roads have fallen from 14% to 9%, but a missing survey year in 2022
              means the trend line has a gap. On a 13.79km network, even 9% RED is roughly 1.2km
              of classified road the council's own data says should be considered for maintenance.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">U-roads (367.50km — 85% of network): improving on paper</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">30.1%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">29%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">20%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">18%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">No survey undertaken in 2024</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium mb-4">
              RED U-roads have fallen from 30.1% to 18% — roughly 66km of residential road still in
              the worst category at the last survey. But the council skipped the 2024 U-road survey
              entirely, and warns the improving trend may not reflect reality.
            </p>
            <QuoteCard
              quote="The trend in the above tables indicates that U-roads in the borough appear to be improving based on surface level condition score. However, this data may not always capture underlying deterioration or the compounding effects of historical underinvestment, which can mask the true long-term condition of the roads."
              source="Newham Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          {/* Funding gap */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">The £11 Million Admission</h3>
            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-rose-400">£11m</div>
                <div className="text-brand-300 text-sm">Minimum annual budget needed (2023 projection)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">£10m</div>
                <div className="text-brand-300 text-sm">Average annual spend since 2016</div>
              </div>
            </div>
            <QuoteCard
              quote="Based on projection modelling carried out in 2023, it was calculated that a minimum annual budget of £11million is required to maintain the network at its current condition. Without this level of investment, the condition of the road network will continue to decline."
              source="Newham Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>
        </div>
      </section>

      {/* Survey blind spot */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 367km Alternate-Year Gap
          </h2>
          <p className="text-surface-600 mb-8">
            How Newham condition-surveys its unclassified network — and what it means for Section 58
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Councils are required to undertake a survey across 100% of their network over a two-year period. It is for this reason Newham undertakes condition survey 50% each year of the unclassified road network, split geographically each year."
              source="Newham Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Detailed Visual Inspection (DVI)</h3>
              <p className="text-surface-600 text-sm mb-3">
                U-road condition data comes from walked inspections by independent surveyors, scored
                under the UK Pavement Management System (UKPMS) standard. The survey identifies
                defects including cracks, potholes, loose slabs, and uneven surfaces.
              </p>
              <p className="text-surface-600 text-sm">
                Classified roads use SCANNER laser-based surveys. From 2026/27, councils must switch
                to the BSI PAS 2161 standard with five condition categories instead of three.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Routine Safety Inspections</h3>
              <p className="text-amber-800 text-sm mb-3">
                Separate from condition surveys, Newham's reactive maintenance team inspects the
                network on a fixed schedule. Local Access Roads — the category most residential
                streets fall into — receive carriageway inspections every 12 months.
              </p>
              <p className="text-amber-800 text-sm">
                Defects found during inspections are raised with the highway contractor for repair
                within KPI timeframes: 2 hours for emergencies, 24 hours for urgent defects, 10 working
                days for routine minor potholes, 28 days for complex repairs.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 58 of the Highways Act 1980, Newham must show it had a reasonable system
              for knowing and repairing road defects. For the 367.5km U-road network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road in the 50% surveyed that year, or the geographic half that was not?</li>
              <li>• With no U-road survey in 2024, what condition data existed when your defect formed?</li>
              <li>• If 18% of U-roads were RED at the last survey, what was done about yours specifically?</li>
              <li>• Does a 12-month safety inspection interval explain why a pothole was left unrepaired?</li>
              <li>• Can the council rely on improving surface scores when it admits they may mask deterioration?</li>
            </ul>
            <p className="text-white font-medium">
              A council that surveys half its residential network each year — and skips a year entirely —
              cannot claim comprehensive knowledge of every defect on every street.
            </p>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN Spend — And What It Actually Buys
          </h2>
          <p className="text-surface-600 mb-8">
            Newham's highways maintenance spending figures — capital, revenue, and preventative share
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100 bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">£692k</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£8,853k</td>
                  <td className="px-4 py-3 text-center">£2,200k</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">81%</td>
                  <td className="px-4 py-3 text-center">19%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£213k</td>
                  <td className="px-4 py-3 text-center">£4,237k</td>
                  <td className="px-4 py-3 text-center">£2,278k</td>
                  <td className="px-4 py-3 text-center">66%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">34%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£213k</td>
                  <td className="px-4 py-3 text-center">£8,767k</td>
                  <td className="px-4 py-3 text-center">£1,691k</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">84%</td>
                  <td className="px-4 py-3 text-center">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center text-surface-400">nil</td>
                  <td className="px-4 py-3 text-center">£12,390k</td>
                  <td className="px-4 py-3 text-center">£1,980k</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">86%</td>
                  <td className="px-4 py-3 text-center">14%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center text-surface-400">nil</td>
                  <td className="px-4 py-3 text-center">£8,407k</td>
                  <td className="px-4 py-3 text-center">£2,034k</td>
                  <td className="px-4 py-3 text-center">81%</td>
                  <td className="px-4 py-3 text-center">19%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center text-surface-400">nil</td>
                  <td className="px-4 py-3 text-center">£9,992k</td>
                  <td className="px-4 py-3 text-center">£1,935k</td>
                  <td className="px-4 py-3 text-center">84%</td>
                  <td className="px-4 py-3 text-center">16%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Keeping Newham Moving — 119km Resurfaced</h3>
              <p className="text-emerald-800 text-sm mb-3">
                Since 2016, the Keeping Newham Moving programme has resurfaced 119.27km of carriageway
                — over a quarter of the 432.21km network:
              </p>
              <ul className="text-emerald-800 text-sm space-y-1">
                <li>• Principal roads (A): 13.67km</li>
                <li>• Non-principal roads (B): 4.60km</li>
                <li>• Unclassified roads (U): 101.00km</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2025/26 Planned Investment</h3>
              <ul className="text-surface-600 text-sm space-y-1">
                <li>• £3.6m towards resurfacing 21 roads including 14 footway schemes</li>
                <li>• £3.3m for reconstruction of roads with adverse ground conditions</li>
                <li>• £2m structural maintenance: Silvertown viaduct, NOS East and West Approach</li>
                <li>• Estimated 1,500 potholes via reactive maintenance service</li>
              </ul>
            </div>
          </div>

          <QuoteCard
            quote="This is due to the fact that much of the network is past its intended design life and requires more extensive interventions."
            source="Newham Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            6,050 Carriageway Potholes in Four Years
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled on Newham's carriageways — plus thousands more on footways
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (carriageway)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold">2,352</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,031</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">1,089</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">1,578</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6,050</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Footways and Cycle Tracks Too</h3>
              <p className="text-surface-600 text-sm">
                Newham's report notes it repairs on average a further 3,000 defects in a single year
                on cycle tracks, footpaths, and footways — separate from the carriageway pothole figures
                above. The 2025/26 plan estimates 1,500 carriageway pothole repairs through reactive
                maintenance alone.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">What the Numbers Tell You</h3>
              <p className="text-amber-800 text-sm">
                Even with £10 million a year in planned maintenance, Newham still fills thousands of
                potholes reactively. Defects form between inspections on a network the council describes
                as past its design life — exactly the scenario where prior reports and photographic
                evidence decide claims.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Newham invests seriously — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — capital spend of £8.9m in 2025/26 against £692k DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 81% preventative maintenance share projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Keeping Newham Moving — 119.27km resurfaced since 2016, over a quarter of the network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented KPI response times — 2 hours for emergencies, 24 hours for urgent defects
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED condition falling from 30.1% to 18% at last survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Highway Infrastructure Asset Management Framework approved by Cabinet December 2023
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence with KPI records and asset management policies.
                Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 18% of U-roads still RED at last survey, A-road amber rising to 34%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 85% of network is U-roads surveyed only 50% each year — no U-road survey at all in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits condition scores may mask underlying deterioration and historical underinvestment
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> £11m minimum budget needed vs ~£10m average spend — documented funding gap
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Network past intended design life requiring extensive interventions
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 6,050 carriageway potholes filled in four years — defects still forming between inspections
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a decade-long resurfacing programme, your claim
              lives or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, Newham's online reporting tool) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the 50%-per-year survey gap and 2024 blind spot are your strongest structural arguments</li>
              <li>• Whether the road was in the geographic half surveyed that year — ask under FOI if needed</li>
              <li>• Whether your incident was on a TfL-maintained route (A13, A406, etc.) or council-maintained road</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence,
              and cites Newham's own transparency data — including the council's admission that surface
              scores may not tell the full story.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Newham?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No TfL vs council routing check</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 367km U-road survey-gap documented</li>
                <li>✅ Condition-data caveat cited verbatim</li>
                <li>✅ £11m funding-gap admission included</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Newham</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=newham"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Newham Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Newham spends nearly thirteen times its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect under
                Section 58 of the Highways Act 1980 — not aggregate spend. Newham is AMBER overall because
                condition data shows persistent problems on a borough where 367.5km of unclassified roads
                make up 85% of the carriageway network, and the council itself warns surface-level scores
                may not capture underlying deterioration.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential street in Newham?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads account for 367.5km — 85% of Newham's 432.21km carriageway network.
                The council surveys 50% of U-roads each year, split geographically, meaning half the
                residential network has no fresh condition data in any given year. At the last survey in
                2023, 18% of U-roads were still in RED condition — down from 30.1% in 2020, but still
                roughly one in five residential roads.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Newham's admission that condition data may not tell the full story help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Newham's own transparency report states that improving surface-level condition
                scores on U-roads "may not always capture underlying deterioration or the compounding
                effects of historical underinvestment, which can mask the true long-term condition of the
                roads." That is the council acknowledging its published data may understate the problem —
                which weakens any Section 58 defence that relies solely on survey scores.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Who maintains the A-road where I hit the pothole — Newham or TfL?
              </h3>
              <p className="text-surface-600 text-sm">
                It depends on the road. Transport for London maintains the TfL Road Network in Newham,
                including the A13, A1020 Royal Docks Road, Lower Lea Crossing, A406, and parts of the
                A117. Newham Council maintains the remainder, including the Principal Road Network.
                A-road condition data in the transparency report comes from TfL surveys — check whether
                your incident was on a TfL or council-maintained stretch before submitting a claim.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Newham says it needs £11 million a year — does underfunding strengthen my case?
              </h3>
              <p className="text-surface-600 text-sm">
                It adds context, not an automatic win. Newham's 2023 projection modelling calculated a
                minimum annual budget of £11 million to maintain the network at its current condition,
                while the council averages £10 million annually since 2016. The report states that without
                £11 million, "the condition of the road network will continue to decline." That documents
                a funding gap the council knows about — but you still need evidence the specific defect
                was unreasonably left unrepaired.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.newham.gov.uk/downloads/file/8962/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Newham Council Local Highways Maintenance Transparency Report (May 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
